import React, { useMemo } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGetProductsQuery, useGetCategoriesQuery } from 'state/api';
import { DataTable, formatDateTime } from 'components/shared';

// Builds an XML sitemap entirely from the store's real catalog (products +
// categories fetched via RTK Query) rather than showing a fabricated row.
// "Generate & Download" produces the file client-side; the two former
// upload buttons were removed because no sitemap upload endpoint exists.
const Sitemap = () => {
  const { data: productData, isFetching: productsLoading } = useGetProductsQuery({
    page: 1,
    limit: 1000,
  });
  const { data: categoryData, isFetching: categoriesLoading } = useGetCategoriesQuery();

  const loading = productsLoading || categoriesLoading;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const entries = useMemo(() => {
    const list = [{ loc: `${origin}/`, type: 'page', lastmod: null }];

    (productData?.data || [])
      .filter((p) => p.status !== 'inactive' && p.status !== 'draft')
      .forEach((p) =>
        list.push({
          loc: `${origin}/product/${p.slug || p._id}`,
          type: 'product',
          lastmod: p.updatedAt,
        })
      );

    (categoryData?.data || [])
      .filter((c) => c.status !== 'inactive')
      .forEach((c) =>
        list.push({
          loc: `${origin}/category/${c.slug || c._id}`,
          type: 'category',
          lastmod: c.updatedAt,
        })
      );

    return list;
  }, [productData, categoryData, origin]);

  const handleGenerate = () => {
    const urls = entries
      .map((e) => {
        const lastmod = e.lastmod
          ? `\n    <lastmod>${new Date(e.lastmod).toISOString()}</lastmod>`
          : '';
        return `  <url>\n    <loc>${e.loc}</loc>${lastmod}\n  </url>`;
      })
      .join('\n');
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;

    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sitemap-${new Date().toISOString().slice(0, 10)}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { field: 'loc', headerName: 'URL' },
    { field: 'type', headerName: 'Type' },
    {
      field: 'lastmod',
      headerName: 'Last Modified',
      render: (row) => formatDateTime(row.lastmod),
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Sitemap
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate an XML sitemap of your storefront pages, products and categories
        so search engines can discover and index them.
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        <InfoOutlinedIcon fontSize="small" />
        <Typography variant="body2">
          The sitemap includes {entries.length} URL
          {entries.length === 1 ? '' : 's'} built from your live catalog.
        </Typography>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleGenerate}
          disabled={loading || entries.length === 0}
        >
          Generate &amp; Download
        </Button>
      </Box>

      <DataTable
        columns={columns}
        rows={entries}
        loading={loading}
        emptyText="No indexable pages found to include in the sitemap."
      />
    </Box>
  );
};

export default Sitemap;
